import React, { useState, useEffect } from 'react';
import { EnvelopeIcon, CheckCircleIcon, XCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import api from '../../api/axiosConfig';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const SmtpSettings = () => {
  const { activeBusiness } = useAuth();
  const hasCustomSmtp = activeBusiness?.subscription?.plan?.features?.customSmtp;

  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const [config, setConfig] = useState({
    host: '',
    port: 587,
    email: '',
    password: '',
    isActive: false
  });
  
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/v1/smtp/config');
      if (res.data.success && res.data.data) {
        setConfig(res.data.data);
        setIsConfigured(true);
      }
    } catch (error) {
      console.error('Failed to load SMTP config', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    if (!config.host || !config.port || !config.email || (!config.password && !isConfigured)) {
      return toast.error('Please fill all required fields');
    }
    
    setLoading(true);
    try {
      await api.post('/v1/smtp/config', config);
      toast.success('SMTP Configuration saved');
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!config.host || !config.port || !config.email || (!config.password && !isConfigured)) {
      return toast.error('Please fill all required fields first');
    }

    setTesting(true);
    const loadingToast = toast.loading('Sending test email...');
    try {
      const res = await api.post('/v1/smtp/test', config);
      toast.success(res.data.message, { id: loadingToast });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Test failed', { id: loadingToast });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden mb-6 transition-colors">
      <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center">
            <EnvelopeIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-[16px] font-extrabold text-gray-900 dark:text-white">Custom Email (SMTP)</h2>
            <p className="text-[12px] font-medium text-gray-500">Send invoices and reports from your own email</p>
          </div>
        </div>

        <div className="mt-4 sm:mt-0 flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit border border-gray-200 dark:border-gray-700">
          <label className="flex items-center cursor-pointer px-3 py-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
            <div className="relative flex items-center">
              <input 
                type="checkbox" 
                name="isActive"
                className="sr-only"
                checked={config.isActive}
                disabled={!hasCustomSmtp || config.adminDisabled}
                onChange={(e) => {
                  if (!hasCustomSmtp) return toast.error('Please upgrade your plan to use Custom SMTP');
                  handleChange(e);
                  if (isConfigured) {
                    api.post('/v1/smtp/config', { ...config, isActive: e.target.checked })
                      .then(() => toast.success(e.target.checked ? 'Custom SMTP Enabled' : 'Custom SMTP Disabled'))
                      .catch(() => toast.error('Failed to update status'));
                  }
                }}
              />
              <div className={`block w-10 h-6 rounded-full transition-colors ${config.isActive ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
              <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${config.isActive ? 'transform translate-x-4' : ''}`}></div>
            </div>
            <span className="ml-3 text-[13px] font-bold text-gray-700 dark:text-gray-300">
              {config.isActive ? 'Active' : 'Inactive'}
            </span>
          </label>
        </div>
      </div>
      
      <div className="relative">
        {/* Plan Lock Overlay */}
        {!hasCustomSmtp && (
          <div className="absolute inset-0 z-10 bg-white/60 dark:bg-gray-900/60 backdrop-blur-[2px] flex items-center justify-center rounded-b-2xl">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-red-100 dark:border-red-900/30 max-w-sm text-center transform -translate-y-4">
              <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <LockClosedIcon className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Premium Feature</h3>
              <p className="text-[13px] text-gray-500 mb-6">Custom SMTP is not available on your current plan. Upgrade to send emails from your own domain.</p>
              <button 
                className="w-full py-2.5 bg-gradient-to-r from-red-600 to-red-500 text-white font-bold rounded-xl shadow-[0_4px_14px_0_rgba(220,38,38,0.39)] hover:shadow-[0_6px_20px_rgba(220,38,38,0.23)] transition-all"
                onClick={() => window.location.href = '/subscription'}
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        )}

        <div className={`p-5 grid grid-cols-1 md:grid-cols-2 gap-5 ${!hasCustomSmtp ? 'opacity-30 pointer-events-none select-none' : ''}`}>
          
          {/* Connection Notice */}
          <div className="md:col-span-2">
            {config.adminDisabled && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[13px] font-bold text-red-800 dark:text-red-400">Suspended by Admin</p>
                  <p className="text-[12px] text-red-600 dark:text-red-300">Your custom SMTP has been disabled by the system administrator. Emails will be sent from the platform default.</p>
                </div>
              </div>
            )}
          </div>
          </div>

        <div>
          <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">SMTP Host</label>
          <input 
            type="text" 
            name="host"
            list="client-smtp-hosts"
            value={config.host}
            onChange={(e) => {
              const val = e.target.value;
              let newPort = config.port;
              if (val === 'smtp.gmail.com' || val === 'smtp.mail.yahoo.com' || val === 'smtp-mail.outlook.com') {
                newPort = 587;
              }
              setConfig(prev => ({...prev, host: val, port: newPort}));
            }}
            placeholder="e.g. smtp.gmail.com" 
            className="w-full px-3 py-2 text-[14px] border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <datalist id="client-smtp-hosts">
            <option value="smtp.gmail.com">Google / Gmail</option>
            <option value="smtp-mail.outlook.com">Microsoft Outlook / Office 365</option>
            <option value="smtp.mail.yahoo.com">Yahoo Mail</option>
            <option value="smtp.zoho.in">Zoho Mail (IN)</option>
            <option value="smtp.zoho.com">Zoho Mail (US)</option>
            <option value="smtp.sendgrid.net">SendGrid</option>
            <option value="smtp.mailgun.org">Mailgun</option>
            <option value="email-smtp.us-east-1.amazonaws.com">Amazon SES</option>
          </datalist>
        </div>
        
        <div>
          <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">SMTP Port</label>
          <input 
            type="number" 
            name="port"
            value={config.port}
            onChange={handleChange}
            placeholder="e.g. 587 or 465" 
            className="w-full px-3 py-2 text-[14px] border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        
        <div>
          <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
          <input 
            type="email" 
            name="email"
            value={config.email}
            onChange={handleChange}
            placeholder="your@email.com" 
            className="w-full px-3 py-2 text-[14px] border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        
        <div>
          <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">App Password</label>
          <input 
            type="password" 
            name="password"
            value={config.password}
            onChange={handleChange}
            placeholder={isConfigured ? '******** (Leave empty to keep current)' : 'Enter App Password'} 
            className="w-full px-3 py-2 text-[14px] border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div className="md:col-span-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-3 mt-2">
          <button 
            type="button" 
            onClick={handleTest}
            disabled={testing || loading}
            className="px-4 py-2 text-[13px] font-bold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
          
          <button 
            type="button" 
            onClick={handleSave}
            disabled={loading || testing}
            className="px-4 py-2 text-[13px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
          >
            {loading ? 'Saving...' : (isConfigured ? 'Update Settings' : 'Save Settings')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SmtpSettings;
